import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * BrochureDialog — lead-capture gate before a brochure download.
 * Controlled via the `brochure` prop: { url, program_slug, program_name } | null
 */
export default function BrochureDialog({ brochure, onClose }) {
    const [form, setForm] = useState({ name: "", email: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (brochure) setForm({ name: "", email: "" });
    }, [brochure]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        if (brochure) document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [brochure, onClose]);

    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!brochure) return;
        setSubmitting(true);
        try {
            await api.post("/brochure/lead", {
                name: form.name,
                email: form.email,
                program_slug: brochure.program_slug,
                program_name: brochure.program_name,
                url: brochure.url,
                interest_category: brochure.interest_category,
            });
            // Open the brochure in a new tab after capturing the lead
            window.open(brochure.url, "_blank", "noopener,noreferrer");
            toast.success("Enjoy the brochure — check your inbox for updates");
            onClose();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {brochure && (
                <motion.div
                    key="brochure-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 z-[80] bg-[rgba(6,17,36,0.75)] backdrop-blur-md flex items-center justify-center p-4"
                    onClick={onClose}
                    data-testid="brochure-dialog-overlay"
                >
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, y: 30, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                        className="w-full max-w-md bg-[var(--navy)] border border-[var(--gold)]"
                        role="dialog"
                        aria-modal="true"
                        data-testid="brochure-dialog"
                    >
                        <div className="hairline-b p-6 md:p-8 flex items-start justify-between">
                            <div>
                                <div className="label-mono text-[var(--gold)]">— DOWNLOAD BROCHURE</div>
                                <h3 className="serif-display text-2xl md:text-3xl mt-3 leading-tight">
                                    {brochure.program_name}<span className="text-[var(--gold)]">.</span>
                                </h3>
                                <p className="text-sm text-[var(--ivory)]/70 mt-2">
                                    Tell us where to send it — you'll also get program updates.
                                </p>
                            </div>
                            <button aria-label="Close" onClick={onClose} className="text-[var(--ivory)]/60 hover:text-[var(--ivory)] transition-colors" data-testid="brochure-dialog-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={submit} className="p-6 md:p-8 space-y-6" data-testid="brochure-form">
                            <div>
                                <label className="label-mono block mb-2">Name</label>
                                <input required className="input-editorial" value={form.name} onChange={change("name")} data-testid="brochure-name" />
                            </div>
                            <div>
                                <label className="label-mono block mb-2">Email</label>
                                <input required type="email" className="input-editorial" value={form.email} onChange={change("email")} data-testid="brochure-email" />
                            </div>
                            <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="brochure-submit">
                                {submitting ? "Preparing…" : (<>Get the brochure <Download className="w-4 h-4" /></>)}
                            </button>
                            <p className="text-xs text-[var(--ivory)]/50 label-mono flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5" /> We respect your inbox. No spam.
                            </p>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
